import { normalizeRef, resolveOutline } from '~/lib/outline'
import { drawsSourceFill, FILL_NODE_POSE, fillRef } from '~/lib/sourceFill'
import { pinFields } from '~/lib/surfaceField'
import { nodeQuad } from '~/lib/surfaceFrames'
import { fieldFilterOf, settleFieldPins, warmFieldTexture } from './fieldTextures'
import { buildSurfaceDraws } from './shaderSurfaces'

const logger = console.scope('fieldWarm')

// A job that took longer than this baked (a cache hit is well under it): the slice ends there.
const BAKE_MS = 3
// Below this much idle time left, a further job waits for the next slice.
const SLICE_RESERVE_MS = 4

const FILL_STAGE = nodeQuad({ ...FILL_NODE_POSE, scaleX: 1 })

const idle = step =>
	typeof requestIdleCallback === 'function' ? requestIdleCallback(step) : setTimeout(() => step(null), 50)

export const fieldWarmJobs = scenes => {
	const jobs = []
	for (const objects of scenes) {
		if (!objects) continue
		const env = { objects }
		for (const obj of Object.values(objects)) {
			if (!drawsSourceFill(obj)) continue
			const ref = fillRef(obj)
			if (resolveOutline(normalizeRef(ref), env)) jobs.push({ ref, env, stage: FILL_STAGE })
		}
	}
	return jobs
}

// The settled warm set (field keys). A warm in flight pins its keys on top of it, and replaces it when it finishes.
let settled = new Set()
let generation = 0

const warmJob = ({ ref, env, stage }, keys, renderer, filter) => {
	const draw = buildSurfaceDraws([ref], env, stage).draws[0]
	if (!draw) return
	if (!keys.has(draw.key)) {
		keys.add(draw.key)
		pinFields([...settled, ...keys])
	}
	const source = filter && warmFieldTexture(draw.key, draw.field, filter)
	if (source) renderer.prepare?.upload(source)?.catch?.(e => logger.warn('field upload failed', e))
}

export const warmFields = (jobs, { schedule = idle, now = () => performance.now() } = {}) => {
	const gen = ++generation
	const keys = new Set()
	let i = 0
	return new Promise(resolve => {
		const finish = () => {
			settled = keys
			pinFields(keys)
			settleFieldPins(keys, fieldFilterOf())
			resolve(true)
		}
		const step = deadline => {
			if (gen !== generation) return resolve(false)
			const renderer = globalThis.pixi_app?.renderer
			// The stage's filter, so a node binds what was uploaded; no renderer means bake only.
			const filter = renderer ? fieldFilterOf() : null
			while (i < jobs.length) {
				const start = now()
				try {
					warmJob(jobs[i++], keys, renderer, filter)
				} catch (e) {
					// One bad outline never stalls the rest: the node reports it when it draws.
					logger.warn('field warm skipped a reference', e)
				}
				if (now() - start > BAKE_MS) break
				if (deadline && deadline.timeRemaining() < SLICE_RESERVE_MS) break
			}
			if (i < jobs.length) schedule(step)
			else finish()
		}
		if (!jobs.length) finish()
		else schedule(step)
	})
}
